import { REQUEST_ID_HEADER } from '@solar/config';
import { API_ERROR_CODES } from '@solar/contracts';
import type { ApiErrorResponse, ApiSuccessResponse } from '@solar/contracts';

import { webConfig } from '../config/env';

import {
  ApiError,
  CLIENT_ERROR_MESSAGES,
  MALFORMED_RESPONSE_CODE,
  NETWORK_ERROR_CODE,
  TIMEOUT_ERROR_CODE,
} from './api-error';

/** Supplies the bearer token for outgoing requests. */
export type AuthTokenProvider = () => string | null;

/**
 * Attempts to obtain a new access token after a 401.
 *
 * Resolves to the new token, or to null when the session is genuinely over.
 * Installed by the authentication provider rather than built in, so the client
 * itself never needs to know how sessions work.
 */
export type TokenRefresher = () => Promise<string | null>;

export interface ApiClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  getAuthToken?: AuthTokenProvider;
  refreshAuthToken?: TokenRefresher;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Correlation id to send, so a UI action can be traced into the API logs. */
  requestId?: string;
  /**
   * Suppresses the refresh-and-retry behaviour.
   *
   * Set on the authentication endpoints themselves: a 401 from `/auth/refresh`
   * means the session is over, and retrying it through the same path would
   * recurse.
   */
  skipAuthRetry?: boolean;
}

interface UnknownRecord {
  [key: string]: unknown;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function isErrorEnvelope(value: unknown): value is ApiErrorResponse {
  return isRecord(value) && value.success === false && isRecord(value.error);
}

function isSuccessEnvelope(value: unknown): value is ApiSuccessResponse<unknown> {
  return isRecord(value) && value.success === true && 'data' in value;
}

/**
 * Typed client for the platform API.
 *
 * Responsibilities: resolve the base URL, attach correlation and (later) auth
 * headers, enforce a timeout, parse JSON defensively and convert every failure
 * mode into an {@link ApiError}. It holds no credentials of its own.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private getAuthToken: AuthTokenProvider | undefined;
  private refreshAuthToken: TokenRefresher | undefined;
  private readonly fetchImpl: typeof fetch | undefined;

  /**
   * The refresh currently in flight, if any.
   *
   * Several queries commonly fail with 401 at the same moment — a dashboard
   * mounting three panels, for instance. Sharing one promise means they all
   * wait on a single refresh rather than each rotating the token and
   * invalidating the others, which reuse detection would read as theft.
   */
  private pendingRefresh: Promise<string | null> | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? webConfig.apiBaseUrl).replace(/\/+$/, '');
    this.timeoutMs = options.timeoutMs ?? webConfig.apiTimeoutMs;
    this.getAuthToken = options.getAuthToken;
    this.refreshAuthToken = options.refreshAuthToken;
    this.fetchImpl = options.fetchImpl;
  }

  /**
   * Connects the client to the session.
   *
   * Called once by the authentication provider. Keeping it out of the
   * constructor lets the shared instance exist before React does.
   */
  configureAuth(options: {
    getAuthToken: AuthTokenProvider;
    refreshAuthToken: TokenRefresher;
  }): void {
    this.getAuthToken = options.getAuthToken;
    this.refreshAuthToken = options.refreshAuthToken;
  }

  /**
   * Resolved per call rather than captured in the constructor, so a test that
   * replaces `globalThis.fetch` still affects the shared client instance.
   */
  private get doFetch(): typeof fetch {
    return this.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get<TData>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<TData> {
    return this.request<TData>(path, { ...options, method: 'GET' });
  }

  post<TData>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method'> = {},
  ): Promise<TData> {
    return this.request<TData>(path, { ...options, method: 'POST', body });
  }

  patch<TData>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method'> = {},
  ): Promise<TData> {
    return this.request<TData>(path, { ...options, method: 'PATCH', body });
  }

  put<TData>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method'> = {},
  ): Promise<TData> {
    return this.request<TData>(path, { ...options, method: 'PUT', body });
  }

  delete<TData>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, 'method'> = {},
  ): Promise<TData> {
    return this.request<TData>(path, { ...options, method: 'DELETE', body });
  }

  postForm<TData>(path: string, body: FormData): Promise<TData> {
    return this.request<TData>(path, { method: 'POST', body });
  }

  async getBlob(path: string): Promise<Blob> {
    try {
      return await this.sendBlob(path);
    } catch (error: unknown) {
      if (
        !(error instanceof ApiError) ||
        error.status !== 401 ||
        this.refreshAuthToken === undefined
      ) {
        throw error;
      }

      const token = await this.refreshOnce();
      if (token === null) throw error;
      return this.sendBlob(path);
    }
  }

  /**
   * Sends a request, refreshing once if the access token has expired.
   *
   * The retry happens at most once per call and never for the authentication
   * endpoints themselves, so a dead session produces one clean 401 rather than
   * a loop.
   */
  async request<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
    try {
      return await this.send<TData>(path, options);
    } catch (error: unknown) {
      const shouldRetry =
        error instanceof ApiError &&
        error.status === 401 &&
        options.skipAuthRetry !== true &&
        this.refreshAuthToken !== undefined;

      if (!shouldRetry) {
        throw error;
      }

      const token = await this.refreshOnce();

      if (token === null) {
        throw error;
      }

      return this.send<TData>(path, { ...options, skipAuthRetry: true });
    }
  }

  /** Runs one refresh at a time and hands the same result to every waiter. */
  private async refreshOnce(): Promise<string | null> {
    this.pendingRefresh ??= (this.refreshAuthToken?.() ?? Promise.resolve(null)).finally(() => {
      this.pendingRefresh = null;
    });

    return this.pendingRefresh;
  }

  private async send<TData>(path: string, options: RequestOptions = {}): Promise<TData> {
    const { method = 'GET', body, signal, requestId } = options;

    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
    }, this.timeoutMs);

    // A caller-provided signal (React Query cancellation) must still work
    // alongside the timeout, so both are forwarded to the same controller.
    const abortFromCaller = (): void => {
      controller.abort();
    };
    signal?.addEventListener('abort', abortFromCaller);

    try {
      const isFormData = body instanceof FormData;
      const response = await this.doFetch(this.resolveUrl(path), {
        method,
        headers: this.buildHeaders(body !== undefined, requestId, isFormData),
        ...(body !== undefined ? { body: isFormData ? body : JSON.stringify(body) } : {}),
        // The refresh token lives in an HttpOnly cookie that JavaScript cannot
        // read, so the browser has to be told to send it cross-origin.
        credentials: 'include',
        signal: controller.signal,
      });

      return await this.parse<TData>(response);
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        throw error;
      }

      // `AbortError` covers both the timeout above and caller cancellation;
      // the two are indistinguishable at this point and both mean "no answer".
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      const code = timedOut ? TIMEOUT_ERROR_CODE : NETWORK_ERROR_CODE;

      throw new ApiError({
        code,
        message: CLIENT_ERROR_MESSAGES[code],
        status: 0,
      });
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  private resolveUrl(path: string): string {
    return `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
  }

  private buildHeaders(hasBody: boolean, requestId?: string, isFormData = false): HeadersInit {
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (hasBody && !isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (requestId !== undefined) {
      headers[REQUEST_ID_HEADER] = requestId;
    }

    const token = this.getAuthToken?.();

    if (token !== null && token !== undefined && token !== '') {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async sendBlob(path: string): Promise<Blob> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await this.doFetch(this.resolveUrl(path), {
        method: 'GET',
        headers: this.buildHeaders(false),
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        await this.parse<never>(response);
        throw new Error('Unreachable response parsing state.');
      }

      return await response.blob();
    } catch (error: unknown) {
      if (error instanceof ApiError) throw error;
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      const code = timedOut ? TIMEOUT_ERROR_CODE : NETWORK_ERROR_CODE;
      throw new ApiError({ code, message: CLIENT_ERROR_MESSAGES[code], status: 0 });
    } finally {
      clearTimeout(timeout);
    }
  }

  private async parse<TData>(response: Response): Promise<TData> {
    const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
    const payload = await readJson(response);

    if (isErrorEnvelope(payload)) {
      throw new ApiError({
        code: payload.error.code,
        message: payload.error.message,
        status: response.status,
        details: payload.error.details,
        requestId: payload.meta?.requestId ?? requestId,
      });
    }

    if (!response.ok) {
      // An error status without the standard envelope: a proxy, a gateway or
      // a crash before the exception filter ran.
      throw new ApiError({
        code: API_ERROR_CODES.INTERNAL_ERROR,
        message: CLIENT_ERROR_MESSAGES[MALFORMED_RESPONSE_CODE],
        status: response.status,
        requestId,
      });
    }

    // Health probes intentionally return an unwrapped body.
    if (isSuccessEnvelope(payload)) {
      return payload.data as TData;
    }

    if (payload === undefined) {
      throw new ApiError({
        code: MALFORMED_RESPONSE_CODE,
        message: CLIENT_ERROR_MESSAGES[MALFORMED_RESPONSE_CODE],
        status: response.status,
        requestId,
      });
    }

    return payload as TData;
  }
}

/** Reads a JSON body without throwing on an empty or malformed payload. */
async function readJson(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '');

  if (text === '') {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** Shared instance configured from the validated public environment. */
export const apiClient = new ApiClient();
